/**
 * What can be done to each CRM entity, declared once through core's
 * `useEntityActions` — so a table row's icons and a detail header's buttons
 * are the same set, with the same labels, permission and dialogs.
 *
 * Every action needs `crm.write`, except the stages', which need `crm.admin`.
 * That is presentation; the server checks again.
 */
import { useTranslation } from 'react-i18next';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReplayIcon from '@mui/icons-material/Replay';
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined';
import TextField from '@mui/material/TextField';
import {
  CRM_LIMITS,
  CRM_PERMISSIONS,
  type ActivityDto,
  type DealDto,
  type OrganizationDto,
  type PersonDto,
  type StageDto,
} from '@forsys/shared';
import {
  formAction,
  useEntityActions,
  type EntityActionsState,
} from '../../hooks/useEntityActions';
import type { EntityAction } from '../../components/RowActions';
import {
  deleteActivity,
  deleteDeal,
  deleteOrganization,
  deletePerson,
  deleteStage,
  loseDeal,
  reopenDeal,
  reorderStages,
  setActivityDone,
  updateActivity,
  updateDeal,
  updateOrganization,
  updatePerson,
  updateStage,
  winDeal,
} from './api';
import {
  ActivityFields,
  activityChanges,
  activityFormFor,
  activityIncomplete,
  blankActivity,
  BLANK_ORGANIZATION,
  BLANK_PERSON,
  BLANK_STAGE,
  BLANK_DEAL,
  DealFields,
  dealChanges,
  dealFormFor,
  dealIncomplete,
  OrganizationFields,
  organizationChanges,
  organizationFormFor,
  PersonFields,
  personChanges,
  personFormFor,
  StageFields,
  stageChanges,
  stageFormFor,
  stageIncomplete,
  type ActivityForm,
  type DealForm,
  type OrganizationForm,
  type PersonForm,
  type StageForm,
} from './forms';

const WRITE = CRM_PERMISSIONS.WRITE;
const ADMIN = CRM_PERMISSIONS.ADMIN;

const editIcon = <EditOutlinedIcon fontSize="small" />;
const deleteIcon = <DeleteOutlinedIcon fontSize="small" />;

export interface ActionOptions {
  /** After any action succeeds. `key` is the action's: `delete` means the record is gone. */
  onChanged: (key: string) => void;
}

// --- deals -------------------------------------------------------------------

interface LoseForm {
  reason: string;
}

export function useDealActions({ onChanged }: ActionOptions): EntityActionsState<DealDto> {
  const { t } = useTranslation('crm');
  const isOpen = (deal: DealDto) => deal.status === 'open';

  return useEntityActions<DealDto>(
    [
      {
        kind: 'immediate',
        key: 'win',
        label: t('deal.action.win'),
        icon: <EmojiEventsOutlinedIcon fontSize="small" />,
        permission: WRITE,
        primary: true,
        applies: isOpen,
        run: (deal) => winDeal(deal.id),
      },
      formAction<DealDto, LoseForm>({
        kind: 'form',
        key: 'lose',
        label: t('deal.action.lose'),
        icon: <ThumbDownOffAltOutlinedIcon fontSize="small" />,
        permission: WRITE,
        applies: isOpen,
        blank: { reason: '' },
        formFor: () => ({ reason: '' }),
        fields: (form, patch) => (
          <TextField
            label={t('field.lostReason')}
            value={form.reason}
            onChange={(event) => patch({ reason: event.target.value })}
            slotProps={{ htmlInput: { maxLength: CRM_LIMITS.lostReasonLength } }}
            helperText={t('hint.lostReason')}
            autoFocus
            fullWidth
          />
        ),
        title: (deal) => t('deal.loseDialog.title', { title: deal.title }),
        submitLabel: t('deal.action.lose'),
        maxWidth: 'xs',
        run: (deal, form) => loseDeal(deal.id, form.reason.trim() || null),
      }),
      {
        kind: 'immediate',
        key: 'reopen',
        label: t('deal.action.reopen'),
        icon: <ReplayIcon fontSize="small" />,
        permission: WRITE,
        applies: (deal) => !isOpen(deal),
        run: (deal) => reopenDeal(deal.id),
      },
      formAction<DealDto, DealForm>({
        kind: 'form',
        key: 'edit',
        label: t('deal.action.edit'),
        icon: editIcon,
        permission: WRITE,
        blank: BLANK_DEAL,
        formFor: dealFormFor,
        fields: (form, patch) => <DealFields form={form} patch={patch} />,
        incomplete: dealIncomplete,
        title: (deal) => t('deal.editDialog.title', { title: deal.title }),
        run: async (deal, form) => {
          const changes = dealChanges(deal, form);
          return Object.keys(changes).length === 0 ? { deal } : updateDeal(deal.id, changes);
        },
      }),
      {
        kind: 'confirm',
        key: 'delete',
        label: t('deal.action.delete'),
        icon: deleteIcon,
        tone: 'error',
        permission: WRITE,
        title: (deal) => t('deal.deleteDialog.title', { title: deal.title }),
        message: t('deal.deleteDialog.message'),
        confirmLabel: t('deal.action.delete'),
        run: (deal) => deleteDeal(deal.id),
      },
    ],
    { onChanged: (_result, key) => onChanged(key) },
  );
}

// --- organizations -----------------------------------------------------------

export function useOrganizationActions({ onChanged }: ActionOptions): EntityActionsState<OrganizationDto> {
  const { t } = useTranslation('crm');
  return useEntityActions<OrganizationDto>(
    [
      formAction<OrganizationDto, OrganizationForm>({
        kind: 'form',
        key: 'edit',
        label: t('organization.action.edit'),
        icon: editIcon,
        permission: WRITE,
        blank: BLANK_ORGANIZATION,
        formFor: organizationFormFor,
        fields: (form, patch) => <OrganizationFields form={form} patch={patch} />,
        incomplete: (form) => form.name.trim() === '',
        title: (organization) => t('organization.editDialog.title', { name: organization.name }),
        run: async (organization, form) => {
          const changes = organizationChanges(organization, form);
          return Object.keys(changes).length === 0 ? { organization } : updateOrganization(organization.id, changes);
        },
      }),
      {
        kind: 'confirm',
        key: 'delete',
        label: t('organization.action.delete'),
        icon: deleteIcon,
        tone: 'error',
        permission: WRITE,
        title: (organization) => t('organization.deleteDialog.title', { name: organization.name }),
        message: t('organization.deleteDialog.message'),
        confirmLabel: t('organization.action.delete'),
        run: (organization) => deleteOrganization(organization.id),
      },
    ],
    { onChanged: (_result, key) => onChanged(key) },
  );
}

// --- persons -----------------------------------------------------------------

export function usePersonActions({ onChanged }: ActionOptions): EntityActionsState<PersonDto> {
  const { t } = useTranslation('crm');
  return useEntityActions<PersonDto>(
    [
      formAction<PersonDto, PersonForm>({
        kind: 'form',
        key: 'edit',
        label: t('person.action.edit'),
        icon: editIcon,
        permission: WRITE,
        blank: BLANK_PERSON,
        formFor: personFormFor,
        fields: (form, patch) => <PersonFields form={form} patch={patch} />,
        incomplete: (form) => form.name.trim() === '',
        title: (person) => t('person.editDialog.title', { name: person.name }),
        run: async (person, form) => {
          const changes = personChanges(person, form);
          return Object.keys(changes).length === 0 ? { person } : updatePerson(person.id, changes);
        },
      }),
      {
        kind: 'confirm',
        key: 'delete',
        label: t('person.action.delete'),
        icon: deleteIcon,
        tone: 'error',
        permission: WRITE,
        title: (person) => t('person.deleteDialog.title', { name: person.name }),
        message: t('person.deleteDialog.message'),
        confirmLabel: t('person.action.delete'),
        run: (person) => deletePerson(person.id),
      },
    ],
    { onChanged: (_result, key) => onChanged(key) },
  );
}

// --- activities --------------------------------------------------------------

/**
 * An activity's row actions without `done` / `undone` — for a list that shows
 * those as `ActivityDoneCheckbox` instead of as icons.
 */
export function withoutDone(actions: readonly EntityAction[]): EntityAction[] {
  return actions.filter((action) => action.key !== 'done' && action.key !== 'undone');
}

export function useActivityActions({ onChanged }: ActionOptions): EntityActionsState<ActivityDto> {
  const { t } = useTranslation('crm');
  return useEntityActions<ActivityDto>(
    [
      {
        kind: 'immediate',
        key: 'done',
        label: t('activity.action.done'),
        icon: <CheckCircleOutlinedIcon fontSize="small" />,
        permission: WRITE,
        applies: (activity) => activity.doneAt === null,
        run: (activity) => setActivityDone(activity.id, true),
      },
      {
        kind: 'immediate',
        key: 'undone',
        label: t('activity.action.undone'),
        icon: <RadioButtonUncheckedIcon fontSize="small" />,
        permission: WRITE,
        applies: (activity) => activity.doneAt !== null,
        run: (activity) => setActivityDone(activity.id, false),
      },
      formAction<ActivityDto, ActivityForm>({
        kind: 'form',
        key: 'edit',
        label: t('activity.action.edit'),
        icon: editIcon,
        permission: WRITE,
        blank: blankActivity(),
        formFor: activityFormFor,
        fields: (form, patch) => <ActivityFields form={form} patch={patch} />,
        incomplete: activityIncomplete,
        title: (activity) => t('activity.editDialog.title', { subject: activity.subject }),
        run: async (activity, form) => {
          const changes = activityChanges(activity, form);
          return Object.keys(changes).length === 0 ? { activity } : updateActivity(activity.id, changes);
        },
      }),
      {
        kind: 'confirm',
        key: 'delete',
        label: t('activity.action.delete'),
        icon: deleteIcon,
        tone: 'error',
        permission: WRITE,
        title: (activity) => t('activity.deleteDialog.title', { subject: activity.subject }),
        message: t('activity.deleteDialog.message'),
        confirmLabel: t('activity.action.delete'),
        run: (activity) => deleteActivity(activity.id),
      },
    ],
    { onChanged: (_result, key) => onChanged(key) },
  );
}

// --- stages ------------------------------------------------------------------

/** The stages' actions need the whole order: moving one is a reorder of all. */
export function useStageActions(
  stages: readonly StageDto[],
  { onChanged }: ActionOptions,
): EntityActionsState<StageDto> {
  const { t } = useTranslation('crm');

  const swap = (stage: StageDto, by: -1 | 1): number[] => {
    const ids = stages.map((one) => one.id);
    const at = ids.indexOf(stage.id);
    const other = ids[at + by];
    if (other === undefined) return ids;
    ids[at + by] = stage.id;
    ids[at] = other;
    return ids;
  };

  return useEntityActions<StageDto>(
    [
      {
        kind: 'immediate',
        key: 'up',
        label: t('stage.action.up'),
        icon: <ArrowUpwardIcon fontSize="small" />,
        permission: ADMIN,
        lockedReason: (stage) => (stages[0]?.id === stage.id ? t('stage.action.first') : null),
        run: (stage) => reorderStages(swap(stage, -1)),
      },
      {
        kind: 'immediate',
        key: 'down',
        label: t('stage.action.down'),
        icon: <ArrowDownwardIcon fontSize="small" />,
        permission: ADMIN,
        lockedReason: (stage) => (stages.at(-1)?.id === stage.id ? t('stage.action.last') : null),
        run: (stage) => reorderStages(swap(stage, 1)),
      },
      formAction<StageDto, StageForm>({
        kind: 'form',
        key: 'edit',
        label: t('stage.action.edit'),
        icon: editIcon,
        permission: ADMIN,
        blank: BLANK_STAGE,
        formFor: stageFormFor,
        fields: (form, patch) => <StageFields form={form} patch={patch} />,
        incomplete: stageIncomplete,
        title: (stage) => t('stage.editDialog.title', { name: stage.name }),
        maxWidth: 'xs',
        run: async (stage, form) => {
          const changes = stageChanges(stage, form);
          return Object.keys(changes).length === 0 ? { stage } : updateStage(stage.id, changes);
        },
      }),
      {
        kind: 'confirm',
        key: 'delete',
        label: t('stage.action.delete'),
        icon: deleteIcon,
        tone: 'error',
        permission: ADMIN,
        title: (stage) => t('stage.deleteDialog.title', { name: stage.name }),
        message: t('stage.deleteDialog.message'),
        confirmLabel: t('stage.action.delete'),
        run: (stage) => deleteStage(stage.id),
      },
    ],
    { onChanged: (_result, key) => onChanged(key) },
  );
}
